import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Filter, Search } from 'lucide-react';

const EMPTY_VALUE = '__empty__';

export function normalizeColumnFilterValue(raw) {
  if (raw === null || raw === undefined) return EMPTY_VALUE;
  const text = String(raw).trim();
  return text ? text : EMPTY_VALUE;
}

export function buildColumnFilterOptions(users, getValue) {
  const counts = new Map();
  for (const entry of users) {
    const value = normalizeColumnFilterValue(getValue(entry));
    counts.set(value, (counts.get(value) || 0) + 1);
  }

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: value === EMPTY_VALUE ? '(Blank)' : value,
      count,
    }))
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }));
}

export function TableColumnFilter({
  columnKey,
  label,
  options,
  selectedValues,
  onChange,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draftSelected, setDraftSelected] = useState(() => new Set());
  const [menuPos, setMenuPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const wasOpenRef = useRef(false);

  const allValues = useMemo(() => options.map((opt) => opt.value), [options]);
  const isActive = useMemo(() => {
    if (!selectedValues || selectedValues.size === 0) return false;
    if (allValues.length === 0) return false;
    return selectedValues.size < allValues.length;
  }, [selectedValues, allValues]);

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(normalized));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeOnScroll = (event) => {
      const scrollTarget = event.target;
      if (scrollTarget instanceof Node && menuRef.current?.contains(scrollTarget)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', closeOnScroll);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', closeOnScroll);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      setQuery('');
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    const initial = selectedValues == null ? new Set(allValues) : new Set(selectedValues);
    setDraftSelected(initial);
  }, [open, selectedValues, allValues]);

  const openMenu = () => {
    if (disabled) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) {
      setOpen(true);
      return;
    }

    const menuWidth = 280;
    const viewportPadding = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const estimatedHeight = Math.min(360, 120 + options.length * 30);
    const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
    const openAbove = spaceBelow < estimatedHeight && rect.top > spaceBelow;
    const top = openAbove
      ? Math.max(viewportPadding, rect.top - estimatedHeight - 8)
      : Math.min(viewportHeight - estimatedHeight - viewportPadding, rect.bottom + 8);
    const left = Math.max(
      viewportPadding,
      Math.min(rect.left, viewportWidth - menuWidth - viewportPadding)
    );

    setMenuPos({ top, left, width: menuWidth });
    setOpen(true);
    document.dispatchEvent(new CustomEvent('table-column-filter:open'));
  };

  const applySelection = (nextSet) => {
    if (nextSet.size >= allValues.length) {
      onChange(columnKey, null);
      return;
    }
    onChange(columnKey, nextSet);
  };

  const toggleValue = (value) => {
    setDraftSelected((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      applySelection(next);
      return next;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(draftSelected);
    filteredOptions.forEach((opt) => next.add(opt.value));
    setDraftSelected(next);
    applySelection(next);
  };

  const clearVisible = () => {
    const next = new Set(draftSelected);
    filteredOptions.forEach((opt) => next.delete(opt.value));
    setDraftSelected(next);
    applySelection(next);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={isActive ? 'table-column-filter-button active' : 'table-column-filter-button'}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          openMenu();
        }}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
        disabled={disabled}
        title={`Filter ${label}`}
      >
        <Filter size={13} />
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && menuPos && (
              <motion.div
                ref={menuRef}
                className="table-column-filter-menu"
                style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                onClick={(event) => event.stopPropagation()}
              >
                <motion.div className="table-column-filter-header">
                  <strong>{label}</strong>
                  <span className="hint-text">{options.length} values</span>
                </motion.div>

                <div className="table-column-filter-search">
                  <Search size={11} aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search..."
                    aria-label={`Search ${label} values`}
                  />
                </div>

                <motion.div className="table-column-filter-actions">
                  <button type="button" className="ghost-button admin-inline-button compact" onClick={selectAllVisible}>
                    Select visible
                  </button>
                  <button type="button" className="ghost-button admin-inline-button compact" onClick={clearVisible}>
                    Clear visible
                  </button>
                </motion.div>

                <motion.div className="table-column-filter-list" role="listbox" aria-label={`${label} filter values`}>
                  {filteredOptions.length === 0 ? (
                    <div className="table-column-filter-empty">No values found.</div>
                  ) : (
                    filteredOptions.map((opt) => {
                      const checked = draftSelected.has(opt.value);
                      return (
                        <label key={opt.value} className="table-column-filter-item">
                          <input
                            type="checkbox"
                            className="table-column-filter-checkbox"
                            checked={checked}
                            onChange={() => toggleValue(opt.value)}
                          />
                          <span className="table-column-filter-item-label">{opt.label}</span>
                          <span className="table-column-filter-item-count">{opt.count}</span>
                        </label>
                      );
                    })
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
